import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useSelectedDealer } from "components/DealersAndLocationsDropdown/hooks";
import { CUSTOMER_TYPES } from "models";
import { DATE_ORDER_BY, WO_FILTERS } from "modules/Appointments";

export type AppointmentTableHeaderDropdownOption<T> = {
  key: T;
  value: T;
  text: string;
  icon?: string;
};

type AppointmentTableHeaderDropdownOptions = {
  date: AppointmentTableHeaderDropdownOption<DATE_ORDER_BY>[];
  customer: AppointmentTableHeaderDropdownOption<CUSTOMER_TYPES>[];
  lastUser: AppointmentTableHeaderDropdownOption<string>[];
  wo: AppointmentTableHeaderDropdownOption<WO_FILTERS>[];
  carMake: AppointmentTableHeaderDropdownOption<string>[];
};

export const useHeaderDropdownOptions = (carMakeValues: string[]): AppointmentTableHeaderDropdownOptions => {
  const { t } = useTranslation();

  const { selectedDealer } = useSelectedDealer();

  return useMemo(() => {
    const date = [
      {
        key: DATE_ORDER_BY.APPOINTMENT_DATE_ASCENDING,
        value: DATE_ORDER_BY.APPOINTMENT_DATE_ASCENDING,
        text: t("v8_appointment_date", "Appointment Date"),
        icon: "arrow down a z"
      },
      {
        key: DATE_ORDER_BY.APPOINTMENT_DATE_DESCENDING,
        value: DATE_ORDER_BY.APPOINTMENT_DATE_DESCENDING,
        text: t("v8_appointment_date", "Appointment Date"),
        icon: "arrow up a z"
      },
      ...(selectedDealer?.enable_car_ready
        ? [
            {
              key: DATE_ORDER_BY.SCHEDULED_OUT_DATE_ASCENDING,
              value: DATE_ORDER_BY.SCHEDULED_OUT_DATE_ASCENDING,
              text: t("v8_scheduled_out", "Scheduled Out"),
              icon: "arrow down a z"
            },
            {
              key: DATE_ORDER_BY.SCHEDULED_OUT_DATE_DESCENDING,
              value: DATE_ORDER_BY.SCHEDULED_OUT_DATE_DESCENDING,
              text: t("v8_scheduled_out", "Scheduled Out"),
              icon: "arrow up a z"
            }
          ]
        : [])
    ];

    return {
      date,
      customer: [
        { key: CUSTOMER_TYPES.Driver, value: CUSTOMER_TYPES.Driver, text: t("v8_driver_name", "Driver Name") },
        { key: CUSTOMER_TYPES.Contractor, value: CUSTOMER_TYPES.Contractor, text: t("v8_contractor_name", "Contractor Name") },
        { key: CUSTOMER_TYPES.Owner, value: CUSTOMER_TYPES.Owner, text: t("v8_owner_name", "Owner Name") }
      ],
      lastUser: [
        { key: "last_user", value: "last_user", text: t("v8_last_updated_by", "Last Updated By") },
        ...(selectedDealer?.onei_planning_enabled || selectedDealer?.planit_planning_enabled
          ? [{ key: "assigned_to", value: "assigned_to", text: t("v8_assigned_to", "Assigned to") }]
          : []),
        { key: "customer_owner", value: "customer_owner", text: t("v8_customer_owner", "Customer Owner") },
        { key: "receptionist_changed", value: "last_receptionist_update_by", text: t("v8_receptionist", "Receptionist") },
        { key: "my_appointments", value: "my_appointments", text: t("v8_my_appointments", "My Appointments") }
      ],
      wo: [
        { key: WO_FILTERS.ALL, value: WO_FILTERS.ALL, text: t("v8_all", "All") },
        { key: WO_FILTERS.WO, value: WO_FILTERS.WO, text: t("v8_WO", "WO") },
        { key: WO_FILTERS.INTERNAL, value: WO_FILTERS.INTERNAL, text: t("v8_internal", "Internal") }
      ],
      carMake: carMakeValues.length
        ? [{ key: "All", value: "", text: t("v8_all", "All") }].concat(...carMakeValues.map(make => ({ key: make, value: make, text: make })))
        : []
    };
  }, [t, selectedDealer?.enable_car_ready, selectedDealer?.onei_planning_enabled, selectedDealer?.planit_planning_enabled, carMakeValues]);
};
